'use client';

import { cn, DialogDescription, DialogFooter, DialogTitle, SheetDescription, SheetFooter, SheetTitle } from '@shared/components/shadcn';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import type { UseEducationalTutorialReturn } from '../../hooks/useEducationalTutorial';
import { EducationalTutorialActions } from '../Actions/EducationalTutorialActions';
import { EducationalTutorialProgress } from '../Progress/EducationalTutorialProgress';

const VARIANTS = {
  modal: {
    Title: DialogTitle,
    Description: DialogDescription,
    Footer: DialogFooter,
    titleClassName: 'text-left',
    descriptionClassName: 'min-h-15',
    footerProps: { className: 'sm:justify-between items-center pt-3' },
  },
  sheet: {
    Title: SheetTitle,
    Description: SheetDescription,
    Footer: SheetFooter,
    titleClassName: 'heading-5',
    descriptionClassName: 'paragraph-explanatory min-h-15',
    footerProps: { className: 'flex-row justify-between items-center p-0 pt-4', 'data-track-container': 'Actions' },
  },
} as const;

type EducationalTutorialContentProps = ComponentProps<'div'> & {
  tutorial: UseEducationalTutorialReturn;
  variant: keyof typeof VARIANTS;
};

function EducationalTutorialContent({ tutorial, variant, className, ...props }: EducationalTutorialContentProps) {
  const t = useTranslations('global_dialogs.educational');
  const { Title, Description, Footer, titleClassName, descriptionClassName, footerProps } = VARIANTS[variant];
  const showProgress = tutorial.totalSteps > 1;
  const hasSectionTitles = tutorial.sections.some(section => section.titleKey);

  return (
    <div className={cn('flex flex-col gap-3', className)} {...props}>
      <Title className={cn(titleClassName, hasSectionTitles && 'sr-only')}>{t(`${tutorial.stepKey}.title`)}</Title>
      <Description asChild className={descriptionClassName}>
        <div className="flex flex-col gap-3">
          {tutorial.sections.map(section => (
            <div key={section.descriptionKey}>
              {section.titleKey && <h3 className="heading-5 text-foreground">{t(section.titleKey)}</h3>}
              <p className={cn(section.titleKey && 'paragraph-explanatory text-foreground-subtle')}>
                {t.rich(section.descriptionKey, {
                  link: chunks => (
                    <Link href={ROUTES.MY_ACCOUNT.SETTINGS()} className="link-label-explanatory" data-testid="educational-settings-link" onClick={tutorial.closeDialog}>
                      {chunks}
                    </Link>
                  ),
                })}
              </p>
            </div>
          ))}
        </div>
      </Description>
      <Footer {...footerProps}>
        {showProgress && <EducationalTutorialProgress tutorial={tutorial} />}
        <EducationalTutorialActions tutorial={tutorial} />
      </Footer>
    </div>
  );
}

export { EducationalTutorialContent };
